import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { QuestionPreviewComponent } from '../question-preview/question-preview.component';
import { QuestionTypeComponent } from '../shared/question-type/question-type.component';
import { DifficultyComponent } from '../shared/difficulty/difficulty.component';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { catchError, delay, finalize, of, Subscription, tap } from 'rxjs';
import { TopicRecommendationPayload } from '@edtech-ui/core/models/quiz-creation/topic-generator';
import { DifficultyLevelQuiz, InputTypeQuiz } from '@edtech-ui/constants/quiz-type.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { QuizGeneratorPayload } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { CustomValidators } from '@edtech-ui/core/utils/ai-search-validators';
import { FileValidationService } from '@edtech-ui/core/services/file-validation/file-validation.service';
import { ProgressQuizDialogBoxComponent } from '../progress-quiz-dialog-box/progress-quiz-dialog-box.component';
import { MatDialog } from '@angular/material/dialog';
import { QuizProgressPopUpContentType } from '@edtech-ui/constants/quiz-progress-pop-up.enum';
import { AIQuizLimitExceededType } from '@edtech-ui/constants/quiz-limit-type.enum';
import { QuizTotalAiQuestionLimitDialogComponent } from '../quiz-total-ai-question-limit-dialog/quiz-total-ai-question-limit-dialog.component';

@Component({
	selector: 'app-ai-mode',
	standalone: true,
	imports: [QuestionPreviewComponent, QuestionTypeComponent, DifficultyComponent, CommonModule, FormsModule, ReactiveFormsModule,
		MatProgressSpinnerModule
	],
	templateUrl: './ai-mode.component.html',
	styleUrl: './ai-mode.component.scss'
})
export class AiModeComponent implements OnDestroy, OnInit {

	selectedSource: InputTypeQuiz = InputTypeQuiz.Prompt;
	selectedType: QuizQuestionType = QuizQuestionType.MCQ;
	difficulty: DifficultyLevelQuiz = DifficultyLevelQuiz.Easy;
	_InputTypeQuiz = InputTypeQuiz;
	_Difficulty = DifficultyLevelQuiz;

	uploadedFile: File | null = null;
	showTopicSection = false;
	showPreview: boolean = false;
	isLoading: boolean = false;
	filePreviewUrl: string | null = null;

	classroomId!: number;
	quizId!: string;
	subscription = new Subscription();
	quizForm!: FormGroup;

	selectedTopics: string[] = [];
	_topicList: string[] = [];
	url: string = '';
	selectedTopic = '';
	fileName: string = '';
	extracted_s3_url = '';
	num_question: number = 1;
	fileError: string | null = null;
	progress: number = 0;
	QuizProgressPopUpContentType = QuizProgressPopUpContentType;
	AIQuizLimitExceededType = AIQuizLimitExceededType;
	@Output() switchMode = new EventEmitter<'manual' | 'ai' | 'course' | 'csv' |'pdf'>();

	constructor(
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private _fileService: FileValidationService,
		private dialog: MatDialog,
	) {
		this.quizForm = new FormGroup({
			inputType: new FormControl('prompt'),
			prompt: new FormControl(''),
			url: new FormControl(''),
			customPrompt: new FormControl(''),
			customTopic: new FormControl(''),
			questionCount: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(5)]),
			selectedType: new FormControl(this.selectedType),
			difficulty: new FormControl(this.difficulty),
			topic: new FormControl(''),
		})
	}

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		});
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];
			this.fileError = this._fileService.validateFile(file);

			if (this.fileError) {
				this.uploadedFile = null;
				this.fileName = '';
				return;
			}

			this.selectedSource = InputTypeQuiz.File;
			this.quizForm.patchValue({ inputType: 'file' })
			this.uploadedFile = file;
			this.fileName = file.name;
			this.filePreviewUrl = null;
			this.extracted_s3_url = '';
			this.showTopicSection = false;
			this.selectedTopics = [];
		}
	}

	topicRecommendation() {
		this.progress = 0;
		const formValue = this.quizForm.value;
		const prompt = formValue.customPrompt?.trim() || formValue.prompt?.trim();
		this.num_question = formValue.questionCount || 1;
		const url = formValue.url?.trim() || ''

		if (this.selectedSource === InputTypeQuiz.Url && (url.includes('youtube.com') || url.includes('youtu.be'))) {
			this._toastr.warning("YouTube links aren’t supported. Please enter a valid webpage URL");
			return;
		}

		if (this.selectedSource === InputTypeQuiz.Url && !url) {
			this._toastr.warning('Please enter a valid URL.');
			return;
		}

		if (this.selectedSource == InputTypeQuiz.Prompt && !prompt) {
			this._toastr.warning('Please enter a valid prompt.');
			return;
		}

		if (this.selectedSource === InputTypeQuiz.File && !this.uploadedFile) {
			this._toastr.warning('Please upload a valid file.');
			return;
		}

		const dialogRef = this.openProgressQuizTopicDialogBox();
		const interval = setInterval(() => {
			if (this.progress < 90) {
				this.progress += 5;
				dialogRef.componentInstance.progress = this.progress;
			}
		}, 200);

		if (this.selectedSource === InputTypeQuiz.File) {
			const formData = new FormData();
			formData.append('classroom_id', this.classroomId.toString());
			formData.append('quiz_id', this.quizId);
			formData.append('input_type', this.selectedSource);
			formData.append('difficulty_level', this.difficulty);
			formData.append('prompt', prompt || '');
			formData.append('file', this.uploadedFile!);
			formData.append('num_questions', this.num_question.toString());

			if (this.uploadedFile) {
				formData.append('file', this.uploadedFile);
			} else {
				clearInterval(interval);
				dialogRef.close();
				this._toastr.warning('Please upload a valid file before generating topic recommendations.');
				return;
			}

			this.subscription.add(
				this._quizService.postTopicRecommendationWithFile(formData, this.classroomId, this.quizId, this.num_question, this.selectedSource, this.difficulty,
					prompt || '').pipe(
						tap((response: any) => {
							if (response.Success) {
								this.progress = 100;
								dialogRef.componentInstance.progress = this.progress;
								this.extracted_s3_url = response.Data?.extracted_s3_url ?? '';
								this._topicList = response.Data.topics_list || [];
								this.showTopicSection = true;
								return;
							}

							const currentAIQuestion = response.Data?.current_ai_questions ?? 0;
							const maxAIQuestion = response.Data?.max_ai_questions ?? 10;
							const currentTotalQuestion = response.Data.current_total_questions ?? 0;
							const maxTotalQuestion = response.Data.max_total_questions ?? 30;
							const quizCount = this.quizForm.get('questionCount')?.value ?? 1;

							if (response?.Data?.total_limit_exceeded && currentTotalQuestion === maxTotalQuestion && quizCount > 0) {
								this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.TOTAL)
								return;
							}

							if (response?.Data?.ai_limit_exceeded && currentAIQuestion === maxAIQuestion
								&& response.Data?.current_ai_questions === response.Data?.max_ai_questions) {
								this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.AI);
								return;
							}

							if ((currentAIQuestion + quizCount) > maxAIQuestion) {
								this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.BACKEND_MESSAGE, response.Message);
								return;
							}
						}),
						delay(1000),
						finalize(() => {
							clearInterval(interval);
							dialogRef.close();
						}),
						catchError(() => {
							clearInterval(interval);
							dialogRef.close();
							this._toastr.error('Something went wrong while generating the topic recommendation.');
							return of(null);
						})
					).subscribe());
		} else {
			const payload: TopicRecommendationPayload = {
				classroom_id: this.classroomId,
				quiz_id: this.quizId,
				input_type: this.selectedSource,
				difficulty_level: this.difficulty,
				prompt: prompt ?? '',
				url: formValue.url?.trim() || '',
				num_questions: this.num_question,
			};

			this.subscription.add(
				this._quizService.postTopicRecommend(payload, this.classroomId, this.quizId, this.num_question, this.selectedSource, prompt, this.difficulty).pipe(
					tap((response: any) => {
						if (response.Success) {
							this.progress = 100;
							dialogRef.componentInstance.progress = this.progress;
							this.extracted_s3_url = response.Data?.extracted_s3_url ?? '';
							this._topicList = response.Data.topics_list || [];
							this.showTopicSection = true;
							return;
						}

						const currentAIQuestion = response.Data?.current_ai_questions ?? 0;
						const maxAIQuestion = response.Data?.max_ai_questions ?? 10;
						const currentTotalQuestion = response.Data.current_total_questions ?? 0;
						const maxTotalQuestion = response.Data.max_total_questions ?? 30;
						const quizCount = this.quizForm.get('questionCount')?.value ?? 1;

						if (response?.Data?.total_limit_exceeded && currentTotalQuestion === maxTotalQuestion && quizCount > 0) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.TOTAL)
							return;
						}

						if (response?.Data?.ai_limit_exceeded && currentAIQuestion === maxAIQuestion
							&& response.Data?.current_ai_questions === response.Data?.max_ai_questions) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.AI);
							return;
						}

						if ((currentAIQuestion + quizCount) > maxAIQuestion) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.BACKEND_MESSAGE, response.Message);
							return;
						}
					}),
					delay(1000),
					finalize(() => {
						clearInterval(interval);
						dialogRef.close();
					}),
					catchError(() => {
						clearInterval(interval);
						dialogRef.close();
						this._toastr.error('Something went wrong while generating the topic recommendation.');
						return of(null);
					})
				).subscribe());
		}
	}

	generateQuestionWithAI() {
		this.progress = 0;
		const formValue = this.quizForm.value;
		const customPrompt = formValue.customPrompt?.trim() || formValue.prompt?.trim();
		const quizCount = this.num_question;
		const topicPrompt = formValue.customTopic?.trim();
		const selectedType = formValue.selectedType

		if (this.selectedTopics.length === 0 && !topicPrompt) {
			this._toastr.warning('Please select at least one topic or enter a custom topic.');
			return;
		}

		const dialogRef = this.openProgressQuizDialogBox();
		const interval = setInterval(() => {
			if (this.progress < 90) {
				this.progress += 5;
				dialogRef.componentInstance.progress = this.progress;
			}
		}, 200);

		const allTopics = [...this.selectedTopics];
		if (topicPrompt) allTopics.push(topicPrompt);

		if (!this.quizId) {
			this._toastr.error('Missing quiz ID');
			return;
		}

		const payload: QuizGeneratorPayload = {
			classroom_id: this.classroomId,
			input_type: this.selectedSource,
			question_type: selectedType,
			difficulty_level: this.difficulty,
			custom_prompt: customPrompt,
			extracted_s3_url: this.extracted_s3_url,
			num_questions: quizCount,
			unit_titles: [],
			topic_titles: [],
			subtopics: [],
			selected_topics: allTopics,
			quizId: this.quizId,
		}

		this.subscription.add(
			this._quizService.postGenerateQuestion(payload, this.quizId, this.selectedSource,
				quizCount, this.difficulty, selectedType).pipe(
					tap((response: any) => {
						if (response.Success) {
							this.progress = 100;
							dialogRef.componentInstance.progress = this.progress;
							this._quizService.saveQuizData.next(response.Data);

							const userId = localStorage.getItem('user_id');
							if (userId) {
								localStorage.setItem(`${userId}_ai_quiz_data`, JSON.stringify({ questions: response.Data.questions }));
							}
							this.showPreview = true;
							this.closePopup();
						} else {
							this._toastr.error(response.Message);
							clearInterval(interval);
							dialogRef.close();
						}
					}),
					delay(1000),
					finalize(() => {
						clearInterval(interval);
						dialogRef.close();
					}),
					catchError(() => {
						clearInterval(interval);
						dialogRef.close();
						this._toastr.error('Something went wrong while generating the questions.');
						return of(null);
					}),
				).subscribe());
	}


	onTopicClick(topic: string): void {
		const index = this.selectedTopics.indexOf(topic);
		if (index === -1) {
			this.selectedTopics.push(topic);
		} else {
			this.selectedTopics.splice(index, 1);
		}

		this.quizForm.patchValue({
			selectedTopics: this.selectedTopics
		});
	}

	setSourceType(type: InputTypeQuiz) {
		this.selectedSource = type;
		this.difficulty = DifficultyLevelQuiz.Easy;
		this.quizForm.patchValue({
			inputType: type,
			prompt: '',
			url: '',
			questionCount: 1,
			customPrompt: '',
			customTopic: '',
			selectedType: this.selectedType,
			difficulty: this.difficulty
		});

		if (this.uploadedFile) {
			this.uploadedFile = null;
			this.fileName = '';
			this.fileError = null;
			this.extracted_s3_url = '';
		}

		this.showTopicSection = false;
		this.selectedTopics = [];
	}

	closePopup() {
		this.showTopicSection = false;
		this.selectedTopics = [];
		this.difficulty = DifficultyLevelQuiz.Easy;
		this.quizForm.reset({
			inputType: 'prompt',
			questionCount: 1,
			selectedType: this.selectedType,
			difficulty: this.difficulty,
		});
		this.uploadedFile = null;
		this.fileName = '';
		this.fileError = null;
		this.extracted_s3_url = '';
	}

	openProgressQuizTopicDialogBox() {
		return this.dialog.open(ProgressQuizDialogBoxComponent, {
			width: '400px',
			disableClose: true,
			data: { progress: 0, type: QuizProgressPopUpContentType.TOPIC }
		})
	}

	openProgressQuizDialogBox() {
		return this.dialog.open(ProgressQuizDialogBoxComponent, {
			width: '400px',
			disableClose: true,
			data: { progress: 0, type: QuizProgressPopUpContentType.QUESTION }
		})
	}

	openQuizAiLimitDialogBox(limitType: AIQuizLimitExceededType, message?: string) {
		const dialogRef = this.dialog.open(QuizTotalAiQuestionLimitDialogComponent, {
			width: '450px',
			disableClose: true,
			data: { type: limitType, message },
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result?.goToManual) {
				this.switchMode.emit('manual');
			}
		});
		return dialogRef;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}